import React, { useEffect, useRef, useState } from 'react';
import 'styles/main.css';

import { animated, useSpring } from '@react-spring/web';
import { getContent } from 'files/Content.js';
import { loadStripe } from '@stripe/stripe-js';
import moment from 'moment-timezone';
import smoothscroll from 'smoothscroll-polyfill';
import update from 'immutability-helper';

import API from 'files/api.js';
import Abstract from 'classes/Abstract.js';
import { AddEditLead, Connect, LeadDetails, UnassignLeads } from 'managers/Leads.js';
import AlertStack from 'views/AlertStack.js';
import Appearance from 'styles/Appearance.js';
import Appointment from 'classes/Appointment.js';
import BannerStack from 'views/BannerStack.js';
import Content from 'managers/Content.js';
import Cookies from 'js-cookie';
import DatePicker, { DualDatePicker } from 'views/DatePicker.js';
import { ClientPreferences, SurveyMonkeyPreferences } from 'managers/Clients.js';
import DesktopNotification from 'views/DesktopNotification.js';
import { DndProvider } from 'react-dnd';
import { EndIndex } from 'structure/Layer.js';
import Event from 'classes/Event.js';
import Events from 'managers/Events.js';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { JitsiMeeting } from 'managers/Appointments.js';
import Lead from 'classes/Lead.js';
import Loader from 'views/Loader.js';
import Login, { runLogin } from 'views/Login.js';
import { NotesRoute } from 'views/Notes.js';
import Notification from 'classes/Notification.js';
import NotificationCenter from 'files/NotificationCenter.js';
import OverviewItem from 'views/OverviewItem.js';
import QueryString from 'query-string';
import Request from 'files/Request.js';
import RequestAgreement from 'views/RequestAgreement.js';
import Sheet from 'views/Sheet.js';
import Sidebar from 'structure/Sidebar.js';
import Sockets from 'managers/Sockets.js';
import Status from 'classes/Status.js';
import User from 'classes/User.js';
import Utils from 'files/Utils.js';
import { VelocityComponent } from 'velocity-react';
import WebView from 'views/WebView.js';

const ContentManager = Content.new();
const EventsManager = Events.new();
const SocketManager = Sockets.new();
const Stripe = loadStripe(API.stripe);

window.theme = 'light';
window.__forceSmoothScrollPolyfill__ = true;

const App = () => {

    const clientRef = useRef(null);
    const fetching = useRef({});
    const layersRef = useRef(null);
    const overviewProps = useRef({ 
        end_date: moment().endOf('day'), 
        start_date: moment().startOf('day') 
    });
    const selectedSlot = useRef(null);
    const statusCodesRef = useRef(null);
    const userRef = useRef(null);

    const [active, setActive] = useState({ view: null });
    const [activeClient, setActiveClient] = useState(null);
    const [alerts, setAlerts] = useState([]);
    const [banners, setBanners] = useState([]);
    const [container, setContainer] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 16 },
        opacity: 1,
        top: 0
    }));
    const [content, setContent] = useState([]);
    const [datePicker, setDatePicker] = useState(null);
    const [datePickerOverride, setDatePickerOverride] = useState(null);
    const [layers, setLayers] = useState([]);
    const [layerIndex, setLayerIndex] = useState([]);
    const [loader, setLoader] = useState(null);
    const [loading, setLoading] = useState(false);
    const [nonce, setNonce] = useState(moment().unix());
    const [notification, setNotification] = useState(null);
    const [overview, setOverview] = useState(null);
    const [permissions, setPermissions] = useState({});
    const [preflight, setPreflight] = useState(false);
    const [requestAgreement, setRequestAgreement] = useState(null);
    const [route, setRoute] = useState(null);
    const [sheet, setSheet] = useState(null);
    const [sidebar, setSidebar] = useState(-500);
    const [size, setSize] = useState({ width: window.innerWidth, height: window.innerHeight });
    const [socketsConnected, setSocketsConnected] = useState(true);
    const [statusCodes, setStatusCodes] = useState([]);
    const [subscriptions, setSubscriptions] = useState([]);
    const [theme, setTheme] = useState('light');
    const [user, setUser] = useState(null);

    const onActiveClientChange = async () => {
        try {

            // prevent moving forward if no client was provided or if websockets have not yet connected
            if(!activeClient || socketsConnected === false) {
                throw new Error(socketsConnected ? 'no client provided' : 'websockets not connected yet');
            }

            // update client ref and update subscriptions state
            clientRef.current = activeClient;
            setSubscriptions(activeClient.subscriptions || []);

            // notify subscribers of client change
            utils.events.emit('client_change', activeClient);

            // fetch custom status codes and stats overview
            fetchClientStatusCodes();
            fetchOverview();

            // update default client for future logins
            Cookies.set('default_client_id', activeClient.id);

            // loop through namespaces and join client room
            utils.sockets.namespaces.forEach(namespace => {
                utils.sockets.persistEmit(namespace, 'join', { client_id : activeClient.id });
            });

            // set subscriptions for client preference changes and account based notifications
            await utils.sockets.persistOn('clients', 'on_update_preferences', onUpdateClientPreferences);
            await utils.sockets.persistOn('notifications', `on_notification_${userRef.current.user_id}`, onNotification);
            await utils.sockets.persistOn('notifications', `on_notification_level_${userRef.current.level}`, onNotification);

            // print to console the socket join for the active client 
            console.log(`[active_client_change]: joined client ${activeClient.id}`);

        } catch(e) {
            console.error(`[active_client_change]: ${e.message}`);
        }
    }

    const onCloseLayer = layerID => {

        setLayerIndex(layerIndex => update(layerIndex, {
            $apply: ids => ids.filter(id => id !== layerID)
        }))
        setLayers(layers => {
            let updatedLayers = update(layers, {
                $apply: layers => layers.map(layer => {
                    if(layer.id === layerID) {
                        layer.visible = false;
                    }
                    return layer;
                })
            });
            let remainingLayers = updatedLayers.filter(layer => {
                return layer.visible !== false
            });
            if(remainingLayers.length === 0) {
                console.log('layers reset');
                document.body.style.overflowY = 'scroll';
                return [];
            }
            return updatedLayers;
        });
    }

    const onClientSettingsClick = () => {
        utils.layer.open({
            id: `client_preferences_${activeClient.id}`,
            abstract: Abstract.create({
                type: 'client',
                object: activeClient
            }),
            Component: ClientPreferences
        });
    }

    const onAppointmentStatusChange = props => {
        try {

            // prevent moving forward if event data was not provided
            if(!props.id || !props.status) {
                throw new Error('[on_appointment_status_change]: event data not found');
            }

            // update listeners with new target object if applicable
            utils.content.update({
                object: props,
                type: 'appointment_status'
            });

        } catch(e) {
            console.error(`[on_appointment_status_change]: ${e.message}`);
        }
    }

    const onInitNotesEditor = async props => {
        try {

            let { abstract_id, abstract_type, client_id, note_id, token, user_id } = props;

            // prevent moving forward if required variables were not provided
            if(!abstract_id || !abstract_type || !client_id || !token || !user_id) {
                throw new Error('an abstract target, access token, client id, and user id are required when using the notes editor');
            }

            // set client object using client id
            clientRef.current = { id: client_id };
            utils.client.set(clientRef.current);

            // set user object using access token and user id
            userRef.current = { token, user_id };
            setUser(userRef.current);

            // update local state with routing props
            setRoute({ 
                props: {
                    ...props,
                    abstract: Abstract.create({
                        object: { id: abstract_id },
                        type: abstract_type
                    })
                }, 
                theme: 'mobile_app',
                type: 'notes_editor' 
            });

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue setting up the notes editor. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onLayerReposition = ({ id, position }) => {

        let index = layers.findIndex(layer => id === layer.id);
        if(index < 0) {
            console.log('no layer index');
            return;
        }
        setLayers(layers => update(layers, {
            [index]: {
                position: {
                    $set: position
                }
            }
        }))
    }


    const onLeadStatusChange = props => {
        try {

            // prevent moving forward if event data was not provided
            if(!props.id || !props.status) {
                throw new Error('[on_lead_status_change]: event data not found');
            }

            // update listeners with new target object if applicable
            utils.content.update({
                object: props,
                type: 'lead_status'
            });

        } catch(e) {
            console.error(`[on_lead_status_change]: ${e.message}`);
        }
    }
    
    const onLogin = async ({ user }) => {
        try {

            // show full screen loader
            await utils.loader.show();

            // set active user
            userRef.current = user;
            setPermissions(user.permissions);
            setUser(user);

            // set user and active client
            clientRef.current = user.client;
            setActiveClient(clientRef.current);

            // connect to websockets and setup subscriptions
            connectToSockets(user);
            await utils.loader.hide();

            // animate components into view and set default view
            setActive({ view: 'dashboard', subView: null });
            setContainer({ opacity: 1, top: 0 });
            setSidebar(0);

            // process optional post-login query parameters
            onProcessPostLoginQueryParameters();

            // set client level listeners for content changes
            utils.content.subscribe('root', ['status', 'users'], {
                onFetch: type => {
                    if(type === 'status') {
                        fetchClientStatusCodes();
                        return;
                    }
                },
                onUpdate: abstract => {
                    switch(abstract.type) {
                        case 'status':
                        setStatusCodes(codes => {
                            return codes.map(prev => {
                                return prev.id === abstract.getID() ? abstract.object : prev;
                            });
                        });
                        return;

                        case 'user':
                        setUser(() => {
                            if(userRef.current.user_id === abstract.getID()) {
                                abstract.object.token = userRef.current.token;
                                abstract.object.client = userRef.current.client;
                            }
                            return user;
                        });
                        return;
                    }
                }
            });

        } catch(e) {
            utils.loader.hide();
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue preparing your content. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onLoginAsUser = async token => {
        try {

            // set loading flag and run login with single-use token
            await utils.loader.show();
            let result = await Request.post(utils, '/users/', {
                token: decodeURIComponent(token),
                type: 'validate_single_use_login_token'
            });

            // set user manually so login ui doesn't render after preflight flag is updated
            let user = User.create(result.user);
            setUser(user);
            
            // run post-login logic
            onLogin(result);

        } catch(e) {
            utils.loader.hide();
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue completing the login process. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onNavigationChange = ({ level, subView, view }) => {

        // prevent moving forward if the requested destination has already been set
        if((view === active.view && !subView) && subView === active.subView) {
            return;
        }

        // determine if view is meant for a different account type
        if(utils.user.get().level > level) {
            utils.alert.show({
                title: 'Just a Second',
                message: 'Your account is not able to view this information. Please speak with your primary if you have any questions'
            });
            return;
        }

        // loop through view types and determine if a custom action is requried
        switch(view) {

            case 'connect':
            utils.layer.open({
                id: 'connect',
                title: 'Connect',
                Component: Connect
            });
            return;

            case 'leads':
            switch(subView) {
                case 'new':
                onNewLeadClick();
                return;

                case 'unassign_leads':
                utils.layer.open({
                    id: 'unassign_leads',
                    title: 'Unassign Leads',
                    Component: UnassignLeads
                });
                return;
            }
            break;

            case 'surveys_and_surveys':
            switch(subView) {
                case 'survey_monkey':
                utils.layer.open({
                    id: 'survey_monkey_preferences',
                    title: 'Survey Monkey Preferences',
                    Component: SurveyMonkeyPreferences
                });
                return;
            }
            break;

            case 'logout':
            onRequestLogout();
            return;

            case 'settings':
            onClientSettingsClick();
            return;
        }

        // scroll current page to the top and update active route
        window.scrollTo(0, 0);
        setActive({ subView, view });
    }

    const onNewLeadClick = () => {

        // create new lead and set primary user
        let lead = Lead.new();
        lead.user = utils.user.get();

        // open lead details layer
        utils.layer.open({
            abstract: Abstract.create({
                type: 'leads',
                object: lead
            }),
            Component: AddEditLead.bind(this, {
                isNewTarget: true
            }),
            id: 'new_lead'
        });
    }

    const onNewAppointment = () => {
        utils.content.fetch('appointments');
    }

    const onNewEvent = () => {
        utils.content.fetch('events');
    }

    const onNewLead = () => {
        utils.content.fetch('leads');
    }

    const onNotification = data => {
        try {

            // create notification and update notification state
            let notification = Notification.create(data);
            console.log(data);

            // notify subscribers that notifications need to be fetched
            utils.content.fetch('notifications');

            // determine if any other subscribers need to be notified
            switch(notification.category) {
                case 'BACKGROUND_LEAD_ASSIGNMENTS_COMPLETED':
                case 'BACKGROUND_LEAD_UNASSIGNMENTS_COMPLETED':
                utils.content.fetch('leads');
                break;

                case 'NEW_SMS_MESSAGE':
                let lead = layersRef.current.find(layer => layer.abstract && layer.abstract.getTag() === `lead-${data.payload.lead_id}`);
                if(lead) {
                    console.warn(`[on_notification]: new message notification silenced, lead layer already open for ${data.payload.lead_id}`);
                    return;
                }
                break;
            }

            // update local state with new notification for banner display
            setNotification(notification);
            
        } catch(e) {
            console.error(e.message);
        }
    }

    const onOpenLayer = async target => {

        // verify that user has permission to open layer if permissions were provided
        if(target.permissions) {
            let match = target.permissions.find(key => utils.user.permissions.get(key) === false);
            if(match) {
                utils.user.permissions.reject();
                return;
            }
        }

        // move layer to front of stack if layer is already open
        let index = layerIndex.findIndex(id => id === target.id);
        if(index >= 0) {
            setTimeout(() => {
                setLayerIndex(indexes => {
                    let results = update(indexes, {
                        $splice: [[index, 1]],
                        $unshift: [target.id]
                    });
                    return results;
                });
            }, 0);
            return;
        }

        // update layers and indexes after a slight timeout
        setTimeout(() => {

            // update layers list
            setLayers(layers => {
                return update(layers, {
                    $push: [target]
                });
            });

            // update layer indexes list
            setLayerIndex(indexes => {
                return update(indexes, {
                    $unshift: [target.id]
                });
            });
        }, 0);
    }

    const onOpenLayerMultiple = targets => {

        // add layers to stack and setup onMount callback
        // sizes are calculated inside of the layer and returned through the onMount callback
        setTimeout(() => {
            setLayers(layers => {
                return update(layers, {
                    $push: targets.map((layer, index) => {
                        layer.onMount = ({ getLayerSizingWidth }) => {
                            let width = getLayerSizingWidth();
                            let center = window.innerWidth / 2;
                            return {
                                position: {
                                    y: 12,
                                    x: index === 0 ? ((center - width) - 6) : (center + 6)
                                }
                            }
                        }
                        return layer;
                    })
                });
            });
            setLayerIndex(layerIndex => {
                return update(layerIndex, {
                    $unshift: targets.map(layer => layer.id)
                });
            });
        }, 0)
    }

    const onPermissionsChange = data => {

        // update local state with new permissions data
        setPermissions({ ...data });
        setUser(user => {
            user.permissions = data;
            return user;
        });

        // notify user that permissions have changed if development environment is not enabled
        if(API.dev_env === false) {
            utils.alert.show({
                title: 'Permissions Updated',
                message: `The permissions for your account have been updated. ${userRef.current.level > User.levels.get().primary ? 'Please speak with your primary if you have any questions.' : 'Please contact support if you have any questions.'}`
            });
        }
    }

    const onProcessPostLoginQueryParameters = () => {

        // determine next steps for query route parameter
        let parameters = QueryString.parse(window.location.search);
        switch(parameters.route) {

            case 'lead_details':
            onShowLeadDetails(parameters.lead_id);
            break;

            case 'single_sign_on':
            onRequestAutoLoginPreference(userRef.current.refresh_token);
            break;

            // determine if a lead id was provided in the query without a route, this is depreciated
            default:
            if(parameters.lead_id) {
                onShowLeadDetails(parameters.lead_id);
            }
        }

        //http://localhost:3000/?action=join_meeting&id=1772117934.UIXjfz&token=eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiIsImtpZCI6ImxpZmV3aXBlLWtleS0xIn0.eyJhdWQiOiJsaWZld2lwZSIsImNvbnRleHQiOnsiYXBwb2ludG1lbnRfaWQiOjIsInVzZXIiOnsibW9kZXJhdG9yIjp0cnVlfX0sImV4cCI6MTc3MjM4MDgwMCwiaXNzIjoibGlmZXdpcGUiLCJyb29tIjoiMTc3MjExNzkzNC5VSVhqZnoiLCJzdWIiOiJqaXRzaS5saWZlLXdpcGUuY29tIn0.mVKV6zu611wBScOYJtEJo1q--3bahBd1WcKXBHy_XPk

        // determine if specific actions need to be taken for query parameters related to the mobile app
        switch(parameters.action) {
            case 'join_meeting':
            onJoinMeeting(parameters.id, parameters.token);
            break;
        }

        // remove query parameters from the query string
        window.history.pushState(null, 'Applied Fire Technologies', '/');
    }

    const onJoinMeeting = async (meetingID, token) => {
        try {

            // submit token to server to validation and retrieve appointment details
            let { appointment } = await Request.post(utils, '/appointments/', {
                token,
                type: 'validate_meeting_token'
            });

            // open meeting layer with jitsi meeting component
            utils.layer.open({
                abstract: Abstract.create({
                    object: appointment,
                    type: 'appointments'
                }),
                Component: JitsiMeeting.bind(this, { token }),
                id: `jitsi_meeting_${meetingID}`
            });

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue joining the meeting. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onRequestAutoLoginPreference = token => {
        utils.alert.show({
            title: 'Remember My Login',
            message: 'Would you like us to automatically log you into this account next time you visit the website?',
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'confirm') {

                    // update cookies with access token for cross-platform logins
                    let preferences = utils.lifewipe.preferences.get();
                    preferences.auto_login = true;
                    preferences.last_login = moment().unix();
                    preferences.refresh_token = token;
                    utils.lifewipe.preferences.set(preferences);
                    return;
                }
            }
        });
    }

    const onRequestLogout = () => {
        utils.alert.show({
            title: 'Logout',
            message: 'Are you sure that you want to logout of your account?',
            buttons: [{
                key: 'logout',
                title: 'Logout',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: async key => {
                if(key === 'logout') {
                    onRunLogout();
                    return;
                }
            }
        });
    }

    const onRestrictedTextClick = evt => {
        evt.stopPropagation();
        utils.alert.show({
            title: 'Restricted Information',
            message: 'Your client has set this information as hidden for your account. Please speak with your primary if you have questions about this piece of information.'
        });
    }

    const onRunLogout = async options => {
        try {

            // reset alerts if applciable
            if(options?.reset_alerts === true) {
                setAlerts([]);
            }

            // remove refresh token for auto login
            localStorage.removeItem('refresh_token');

            // reset ui back to pre-login state
            setSidebar(-500);
            setLayers([]);
            setLayerIndex([]);
            setContainer({
                top: -200,
                opacity: 0
            });

            // reset navigation back to pre-login state
            await Utils.sleep(0.25);
            setActive({ view: null });

            // remove user and client details
            utils.client.set(null);
            setUser(null);

        } catch(e){
            console.error(e.message);
        }
    }

    const onSetLayerIndex = layerID => {

        let index = layers.findIndex(layer => layer.id === layerID);
        if(index < 0) {
            console.log('no layer index');
            return;
        }
        setLayers(layers, update(layers, {
            $apply: layers => layers.map(layer => {
                layer.moveToFront = layer.id === layerID;
                return layer;
            })
        }))

        let _index = layerIndex.findIndex(id => id === layerID);
        setLayerIndex(layerIndex, update(layerIndex, {
            $splice: [
                [_index, 1],
                [0, 0, layerID]
            ]
        }))
    }

    const onSetStyleSheetProperties = () => {
        setTheme(window.theme);
        document.body.className = window.theme;
        document.documentElement.style.setProperty('--divider', Appearance.colors.divider());
        document.documentElement.style.setProperty('--green', Appearance.colors.green);
        document.documentElement.style.setProperty('--grey', Appearance.colors.grey());
        document.documentElement.style.setProperty('--soft_border', Appearance.colors.softBorder());
        document.documentElement.style.setProperty('--text', Appearance.colors.text());
        document.documentElement.style.setProperty('--textfield', Appearance.colors.textField());
        document.documentElement.style.setProperty('--theme', window.theme);
        document.querySelector('meta[name="theme-color"]').setAttribute("content", Appearance.colors.background());
    }

    const onShowLeadDetails = async id => {
        try {
            let lead = await Lead.get(utils, id);
            utils.layer.open({
                abstract: Abstract.create({
                    object: lead,
                    type: 'leads'
                }),
                Component: LeadDetails,
                id: `lead_details_${id}`,
                permissions: ['leads.details']
            });
        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the details for this lead. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onSlotChange = slot => {

        // update local state with newly selected slot
        selectedSlot.current = slot;

        // notify subscribers that slot has changed
        utils.events.emit('slot_change', { slot });
    }

    const onUpdateContentComponents = () => {

        const contentHasCapability = entry => {
            if(!entry.capabilities) {
                return true;
            }
            let match = entry.capabilities.find(key => utils.subscriptions.capabilities.get(key) === false);
            return match ? false : true;
        }

        const contentHasPermission = entry => {
            if(!entry.permissions) {
                return true;
            }
            let match = entry.permissions.find(key => utils.user.permissions.get(key) === false);
            return match ? false : true;
        }

        // prepare list of targets and filter out content that does not match the user's permissions
        let content = getContent(utils);
        let targets = Object.keys(content).reduce((array, key) => {

            // validate permissions at a section level if applicable
            if(content[key].permissions) {
                if(contentHasPermission(content[key]) === true) {
                    array.push(content[key]);
                }
                return array;
            }

            // loop through panels and determine if panel level validation needs to occur
            if(content[key].panels) {
                let panels = content[key].panels.filter(panel => contentHasPermission(panel) && contentHasCapability(panel)) || [];
                if(panels.length > 0) {
                    array.push({ ...content[key], panels });
                }
                return array;
            }

            // determine if subview panel validation needs to occur
            if(content[key].subViews) {

                let subViews = {};
                Object.keys(content[key].subViews).forEach(view => {

                    // validate permissions subview level if applciable
                    let target = content[key].subViews[view];
                    if(target.permissions && (contentHasPermission(target) === false || contentHasCapability(target) === false)) {
                        return array;
                    }

                    // no additional logic is required if no panels are set
                    if(!target.panels) {
                        subViews[view] = target;
                        return;
                    }

                    // filter out panels that fail the permissions check and remove subview if all panels fail
                    let panels = target.panels.filter(panel => contentHasPermission(panel) && contentHasCapability(panel)) || [];
                    if(panels.length > 0) {
                        subViews[view] = { ...target, panels };
                    }
                });
                array.push({ ...content[key], subViews });
            }
            return array;
            
        }, []);

        // update local state with new content components
        setContent(targets);
    }

    const onUpdateClientPreferences = () => {
        utils.events.emit('client_preferences_update');
    }

    const onUpdateAppointment = props => {
        try {

            // prevent moving forward if event data was not provided
            if(!props.appointment) {
                throw new Error('[on_update_appointment]: event data not found');
            }

            // update listeners with new target object if applicable
            utils.content.update({
                object: Appointment.create(props.appointment),
                type: 'appointments'
            });

        } catch(e) {
            console.error(`[on_update_appointment]: ${e.message}`);
        }
    }

    const onUpdateEvent = props => {
        try {

            // prevent moving forward if event data was not provided
            if(!props.event) {
                throw new Error('[on_update_event]: event data not found');
            }

            // update listeners with new target object if applicable
            utils.content.update({
                object: Event.create(props.event),
                type: 'events'
            });

        } catch(e) {
            console.error(`[on_update_event]: ${e.message}`);
        }
    }

    const onUpdateLead = props => {
        try {
            
            // prevent moving forward if event data was not provided
            if(!props.lead) {
                throw new Error('[on_update_lead]: event data not found');
            }

            // update listeners with new target object if applicable
            utils.content.update({
                object: Lead.create(props.lead),
                type: 'leads'
            });

        } catch(e) {
            console.error(`[on_update_lead]: ${e.message}`);
        }
    }

    const onUpdateTheme = evt => {
        window.theme = evt.matches ? 'dark' : 'light';
        onSetStyleSheetProperties();
        setNonce(moment().unix());
    }

    const onWindowSizeChange = () => {
        setSize({
            width: window.innerWidth,
            height: window.innerHeight
        });
    }

    const getPanels = () => {

        // prevent moving forward if no primary view is set
        let { view, subView } = active;
        if(!view) {
            return [];
        }

        // prepare an array of the requested panels
        return content.reduce((array, entry) => {

            // skip entry if view doesnt match
            if(entry.key !== view) {
                return array;
            }

            // determine if the requested view contains subviews
            if(subView) {
                return entry.subViews[subView].panels.filter(panel => panel.visible !== false);
            }
            
            // fallback to returning all panels for selected view
            return entry.panels.filter(panel => panel.visible !== false);

        }, []);
    }

    const getRestrictedText = () => {
        return (
            <img
            className={'text-button'}
            onClick={onRestrictedTextClick}
            src={'images/text-content-hidden.png'}
            style={{
                width: 70,
                height: 20,
                objectFit: 'contain'
            }} />
        )
    }

    const getApplicationComponents = () => {
        let panels = getPanels();
        return (
            <main
            className={'main-content col-sm-12 col-md-9 col-lg-10 p-0 offset-lg-2 offset-md-3'}
            style={{
                zIndex: '900'
            }}>
                {getMobileHeader()}
                <animated.div
                className={'main-content-container container-fluid px-0 pt-0'}
                style={{
                    position: 'relative',
                    paddingBottom: 250,
                    ...container
                }}>
                    {getHeaderItems()}
                    <div
                    className={'row w-100 p-0 m-0'}
                    id={'panel-container'}
                    style={{
                        position: 'relative'
                    }}>
                        <DndProvider
                        backend={HTML5Backend}
                        options={{
                            enableTouchEvents: false,
                            enableMouseEvents: true
                        }}>
                            {panels && panels.map(({ Component }, index) => (
                                <Component
                                key={index}
                                utils={utils}
                                index={index} />
                            ))}
                        </DndProvider>
                    </div>
                </animated.div>
            </main>
        )
    }

    const getHeaderItems = () => {

        let codes = Appointment.status.get();

        // prevent moving forward if an overview object is not available
        if(!overview) {
            return null;
        }

        // prepare list of status codes for overview items
        let targets = statusCodes.reduce((acc, entry) => {
            acc[entry.code] = entry;
            return acc;
        }, {});

        // prepare overview items
        let items = [{
            key: 'total',
            title: 'Total Appointments',
            value: overview.total,
            color: Appearance.colors.darkGrey
        },{
            key: 'confirmed',
            title: `Confirmed Appointments`,
            value: overview.confirmed,
            color: targets[codes.confirmed]?.color || Appearance.colors.grey()
        },{
            key: 'rescheduled',
            title: `Rescheduled Appointments`,
            value: overview.rescheduled,
            color: targets[codes.rescheduled]?.color || Appearance.colors.grey()
        },{
            key: 'ran',
            title: `Ran Appointments`,
            value: overview.ran,
            color: targets[codes.set]?.color || Appearance.colors.grey()
        },{
            key: 'sale',
            title: 'Sold Appointments',
            value: overview.sale,
            color: targets[codes.sale]?.color || Appearance.colors.grey()
        }];

        return (
            <div className={'row px-3 py-0 m-0'}>
                {items.map((item, index) => {
                    return content.length > 0 && (
                        <OverviewItem
                        count={overview[item.key]}
                        endDate={overviewProps.current.end_date}
                        index={index}
                        item={item}
                        key={index}
                        loading={loading === 'overview'} 
                        onLoadingChange={setLoading}
                        startDate={overviewProps.current.start_date}
                        utils={utils}/>
                    )
                })}
            </div>
        )
    }

    const getMobileHeader = () => {

        return (
            <nav className={`main-navbar ${theme} navbar navbar-light sticky-top d-flex d-md-none flex-md-nowrap p-0 w-100 text-center`}>
                <div style={{
                    flexDirection: 'row',
                    width: '100%',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <a
                    href={'#'}
                    className={'nav-link nav-link-icon toggle-sidebar d-sm-inline d-md-none d-lg-none text-center'}>
                        <img
                        className={'text-button'}
                        src={`images/navigation-${theme === 'dark' ? 'white' : 'dark-grey'}.png`}
                        style={{
                            width: 25,
                            height: 25,
                            objectFit: 'contain'
                        }}
                        onClick={() => setSidebar(0)} />
                    </a>
                    <div className={'nav-link nav-link-icon toggle-sidebar d-sm-inline d-md-none d-lg-none text-center'}/>
                </div>
            </nav>
        )
    }

    const getLayers = () => {
        return layers.map(({ abstract, id, onMount, options, target, title, visible, Component }, index) => {
            if(visible === false) {
                // must return null instead of using filter
                // using filter for visible does not preseve other visible layers
                return null;
            }
            return (
                <Component
                abstract={abstract}
                index={index}
                key={index}
                title={title}
                utils={utils}
                options={{
                    ...options,
                    index: index,
                    zIndex: EndIndex - layerIndex.findIndex(indexID => id === indexID),
                    onClose: onCloseLayer,
                    onLayerIndexChange: onSetLayerIndex,
                    onMount: onMount,
                    onReposition: onLayerReposition,
                    target: target
                }}/>
            )
        });
    }

    const getMainContent = () => {
        return (
            <>
            {getLayers()}
            <div className={'container-fluid'}>
                <div className={'row'}>
                    {getSidebarComponents()}
                    {getApplicationComponents()}
                </div>
            </div>
            </>
        )
    }

    const getRoutingContent = () => {
        switch(route.type) {
            case 'notes_editor':
            return (
                <>
                {getLayers()}
                <div className={'container-fluid'}>
                    <div className={'row'}>
                        <NotesRoute {...route.props} utils={utils} />
                    </div>
                </div>
                </>
            )
        }
    }

    const getSidebarComponents = () => {
        return (
            <VelocityComponent
            animation={{
                left: sidebar
            }}
            delay={250}
            duration={750}
            easing={[250, 20]}>
                <aside
                className={'main-sidebar col-12 col-md-3 col-lg-2 px-0'}
                style={{
                    borderWidth: 0,
                    zIndex: 1000
                }}>
                    <Sidebar
                    active={active}
                    activeClient={activeClient}
                    content={content}
                    onClick={onNavigationChange} 
                    onLogoutClick={onRequestLogout}
                    onMobileClose={() => setSidebar(-window.innerWidth)}
                    permissions={permissions}
                    user={user}
                    utils={utils}/>
                </aside>
            </VelocityComponent>
        )
    }

    const connectToSockets = async user => {
        try {

            // setup sockets and listeners
            await SocketManager.connect(user);

            // appointment change listeners
            SocketManager.persistOn('leads', 'on_appointment_status_change', onAppointmentStatusChange);
            SocketManager.persistOn('appointments', 'on_new_appointment', onNewAppointment);
            SocketManager.persistOn('appointments', 'on_update_appointment', onUpdateAppointment);

            // notifications for specific user and account types
            SocketManager.persistOn('notifications', `on_notification_${user.user_id}`, onNotification);
            SocketManager.persistOn('notifications', `on_notification_level_${user.level}`, onNotification);

            // event change listeners
            SocketManager.persistOn('events', 'on_new_event', onNewEvent);
            SocketManager.persistOn('events', 'on_update_event', onUpdateEvent);

            // lead change listeners
            SocketManager.persistOn('leads', 'on_new_lead', onNewLead);
            SocketManager.persistOn('leads', 'on_status_change', onLeadStatusChange);
            SocketManager.persistOn('leads', 'on_update_lead', onUpdateLead);

            // user permission changes
            SocketManager.persistOn('users', `on_permissions_change_${user.user_id}`, onPermissionsChange);

            // update root flag for websocket connection
            setSocketsConnected(true);

        } catch(e) {
            console.error(`[websockets]: ${e.message}`);
        }
    }

    const fetchClientStatusCodes = async () => {
        try {
            let { status_codes } = await Request.get(utils, '/clients/', {
                show_global: true,
                type: 'status_codes'
            });
            console.log('status_codes', status_codes);
            setStatusCodes(status_codes.map(status => Status.create(status)));
        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the status codes for this client. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const fetchOverview = async () => {
        try {
            if(!clientRef.current || !userRef.current) {
                return;
            }

            setLoading('overview');
            let response = await Request.get(utils, '/utils/', {
                end_date: overviewProps.current.end_date.format('YYYY-MM-DD'),
                start_date: overviewProps.current.start_date.format('YYYY-MM-DD'),
                type: 'overview'
            });

            setLoading(false);
            setOverview(response);

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue fetching some of the information needed for setup. ${e.message || 'An unknown error occurred'}`
            });
        }
    };

    const findContent = () => {

        // return specialty routing content if applicable
        if(route) {
            return getRoutingContent();
        }

        // return main application content if a user state has been set
        if(user) {
            return getMainContent();
        }

        // determine if pre-login tasks have completed
        if(preflight === false) {
            return null;
        }

        // fallback to showing user login
        return (
            <div style={{
                alignItems: 'center',
                display: 'flex',
                height: '100%',
                justifyContent: 'center',
                padding: 25,
                width: '100%'
            }}>
                <Login
                onLogin={onLogin}
                utils={utils}/>

                {getLayers()}

                {requestAgreement && (
                    <RequestAgreement
                    {...requestAgreement}
                    utils={utils}
                    onClose={() => {
                        setRequestAgreement(null);
                        if(typeof(requestAgreement.onClose) === 'function') {
                            requestAgreement.onClose();
                        }
                    }}/>
                )}
            </div>
        )
    }

    const runPreflightTasks = async () => {
        try {

            // run pre-login tasks  if applicable
            let parameters = QueryString.parse(window.location.search);
            switch(parameters.route) {
                case 'admin_login_as_user':
                case 'single_sign_on':
                return onLoginAsUser(parameters.token);

                case 'notes_editor':
                return onInitNotesEditor(parameters);
            }

            // fetch and decode cross-platform cookie
            let preferences = utils.lifewipe.preferences.get();

            // determine if a token is present and auto login is enabled for the platform
            if(preferences.refresh_token && preferences.auto_login === true) {

                // set loading flag and run login for access token
                await utils.loader.show();
                let result = await runLogin(utils, { refresh_token: preferences.refresh_token });

                // set user manually so login ui doesn't render after preflight flag is updated
                setUser(result.user);
                
                // run post-login logic
                onLogin(result);
            }

            // set preflight flag as complete
            setPreflight(true);

        } catch(e) {
            console.error(e.message);
            setPreflight(true);
            utils.loader.hide();
        }
    }

    const utils = {
        alert: {
            show: async props => {
                try {
                    if(loader) {
                        await utils.loader.hide();
                    }
                    setAlerts(alerts => update(alerts, {
                        $push: [{
                            id: `${moment().unix()}-${Math.random()}`,
                            ...props
                        }]
                    }))
                } catch(e) {
                    console.error(e.message)
                }
            },
            showAsync: async props => {
                return new Promise(async (resolve, reject) => {
                    try {
                        if(loader) {
                            await utils.loader.hide();
                        }
                        setAlerts(alerts => update(alerts, {
                            $push: [{
                                id: `${moment().unix()}-${Math.random()}`,
                                onClose: resolve,
                                ...props
                            }]
                        }))
                    } catch(e) {
                        reject(e.message)
                    }
                })
            },
            dev: async () => {
                try {
                    if(loader) {
                        await utils.loader.hide();
                    }
                    setAlerts(alerts => update(alerts, {
                        $push: [{
                            id: `${moment().unix()}-${Math.random()}`,
                            title: 'In Development',
                            message: 'This feature is currently under development and will become available at a later date'
                        }]
                    }))

                } catch(e) {
                    console.error(e.message)
                }
            },
        },
        agreement: {
            request: props => setRequestAgreement(props)
        },
        api: {
            headers: () => {
                return {
                    'Content-Type': 'application/json',
                    'X-API': `Version ${API.version}`,
                    'X-Timezone': `TZ ${moment.tz.guess()}`,
                    'X-Web': `Build ${API.build}`,
                    ...userRef.current && {
                        'Authorization': `Bearer ${userRef.current.token}`,
                        'Identification': `User ${userRef.current.user_id}`
                    }
                }
            }
        },
        banner: {
            show: props => {
                setBanners(banners => {
                    return update(banners, {
                        $push: [{
                            id: `${moment().unix()}-${Math.random()}`,
                            ...props
                        }]
                    });
                });
            }
        },
        content: ContentManager,
        datePicker: {
            show: props => {
                if(props.overrideAlerts) {
                    setDatePickerOverride({
                        utils: utils,
                        id: `${moment().unix()}-${Math.random()}`,
                        ...props
                    });
                    return;
                }
                setDatePicker({
                    utils: utils,
                    id: `${moment().unix()}-${Math.random()}`,
                    ...props
                })
            },
            showDual: props => {
                utils.layer.open({
                    id: 'dual_date_picker_alert',
                    Component: DualDatePicker.bind(this, {
                        ...props,
                        utils: utils
                    })
                })
            }
        },
        client: {
            get: () => clientRef.current,
            set: async client => {
                try {

                    // loop through namespaces and leave client room
                    utils.sockets.namespaces.forEach(namespace => {
                        utils.sockets.persistEmit(namespace, 'leave', { client_id: activeClient.id });
                    });
                    
                    // remove subscriptions for client preference changes and new notifications
                    await utils.sockets.off('clients', 'on_update_preferences', onUpdateClientPreferences);
                    await utils.sockets.off('notifications', `on_notification_${userRef.current.user_id}`, onNotification);
                    await utils.sockets.off('notifications', `on_notification_level_${userRef.current.level}`, onNotification);

                    // update local state for active client
                    clientRef.current = client;
                    setActiveClient(client);
                    
                } catch(e) {
                    console.error(e.message);
                }
            },
            slot: {
                get: () => selectedSlot.current
            },
            status_codes: {
                fetch: fetchClientStatusCodes,
                get: () => statusCodesRef.current
            },
            update_preferences: prefs => {
                setActiveClient(target => {
                    target.preferences = prefs;
                    return target;
                });
            }
        },
        events: EventsManager,
        fetching: {
            get: key => {
                return fetching.current[key];
            },
            set: (key, value) => {
                fetching.current[key] = value;
            }
        },
        lifewipe: {
            preferences: {
                get: () => {
                    let result = Cookies.get('auto_login_preferences');
                    return typeof(result) === 'string' ? JSON.parse(result) : {};
                },
                set: props => {
                    Cookies.set('auto_login_preferences', JSON.stringify(props));
                }
            }
        },
        layer: {
            open: layer => onOpenLayer(layer),
            openMultiple: layers => onOpenLayerMultiple(layers),
            close: layer => onCloseLayer(layer),
            web: props => onOpenLayer({
                id: props.id,
                Component: WebView.bind(this, props)
            }),
            requestClose: id => {
                utils.events.emit('layer_state_change_request', { 
                    id: id,
                    value: 'close'
                });
            }
        },
        loader: {
            show: async () => {
                return new Promise(resolve => {
                    setLoader(true);
                    setTimeout(resolve, 500)
                })
            },
            hide: async () => {
                return new Promise(resolve => {
                    setLoader(false);
                    setTimeout(resolve, 500)
                })
            }
        },
        notification: {
            show: props => {
                ContentManager.fetch('notifications');
                NotificationCenter.notify(utils, props);
                setNotification(props);
            }
        },
        route: {
            get: () => route || {}
        },
        sheet: {
            show: (sheet, callback) => {
                setSheet({ ...sheet, onClick: callback, utils })
            }
        },
        sockets: SocketManager,
        stripe: Stripe,
        subscriptions: {
            capabilities: {
                get: (key, strict = false) => {

                    // no additional logic is required if user is an administrator
                    if(userRef.current.level <= User.levels.get().admin && strict === false) {
                        return true;
                    }

                    // loop through client subscriptions and look for a key match
                    let match = subscriptions.find(subscription => {
                        return subscription.capabilities.find(capability => key === capability.key);
                    });
                    return match ? true : false;
                },
                reject: (options = {}) => {

                    // prepare rejection message
                    let message = `This feature is not available for your GRACI subscription. ${userRef.current.level > User.levels.get().primary ? `Please speak with your primary about upgrading the client's subscription.` : 'Please upgrade your GRACI subscription if you wish to use this feature.'}`;

                    // determine if a throwable was requested
                    if(options.throw === true) {
                        throw new Error(message);
                    }

                    // fallback to showing rejection alert
                    utils.alert.show({
                        title: 'Just a Second',
                        message: message
                    });
                }
            }
        },
        user: {
            get: () => userRef.current,
            logout: onRunLogout,
            permissions: {
                alert: {
                    
                },
                get: key => {
                    return permissions.values && typeof(permissions.values[key]) === 'boolean' ? permissions.values[key] : true;
                },
                reject: (options = {}) => {

                    // prepare rejection message
                    let message = `This feature has been disabled for your account. ${userRef.current.level > User.levels.get().primary ? 'Please speak with your primary if you have any questions.' : 'Please contact support if you have any questions.'}`;

                    // determine if a throwable was requested
                    if(options.throw === true) {
                        throw new Error(message);
                    }

                    // fallback to showing rejection alert
                    utils.alert.show({
                        title: 'Just a Second',
                        message: message
                    });
                }
            }
        }
    }

    useEffect(() => {
        onActiveClientChange();
    }, [activeClient, socketsConnected]);

    useEffect(() => {
        userRef.current = user;
    }, [user]);

    useEffect(() => {
        layersRef.current = layers;
    }, [layers]);

    useEffect(() => {
        if(permissions) {
            onUpdateContentComponents();
        }
    }, [permissions]);

    useEffect(() => {
        statusCodesRef.current = statusCodes;
    }, [statusCodes]);

    useEffect(() => {

        runPreflightTasks();

        // setup scroll polyfill and window listeners
        smoothscroll.polyfill();
        window.addEventListener('resize', onWindowSizeChange);
        window.addEventListener('beforeunload', (e) => {
            (e || window.event).returnValue = null;
            return null;
        });

        // css variables
        document.documentElement.style.setProperty('--primary', Appearance.colors.primary());
        document.documentElement.style.setProperty('--text', Appearance.colors.text());
        document.documentElement.style.setProperty('--textfield', Appearance.colors.textField());
        document.documentElement.style.setProperty('--soft_border', Appearance.colors.softBorder());

        // theme and theme listeners
        window.theme = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        document.documentElement.style.setProperty('--theme', window.theme);
        window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', onUpdateTheme);
        onSetStyleSheetProperties();

        // window state listeners
        window.addEventListener('focus', () => {
            document.title = 'LifeWipe';
        });

    }, []);

    return (
        <div
        className={`root-container ${window.theme}`}
        nonce={nonce}
        style={{
            height: size.height,
            position: 'relative',
            width: '100%'
        }}>
            {findContent()}
            <Loader animate={loader}/>

            {datePicker && (
                <DatePicker
                {...datePicker}
                onClose={() => {
                    setDatePicker(null);
                    if(typeof(datePicker.onClose) === 'function') {
                        datePicker.onClose();
                    }
                }} />
            )}
            {sheet && (
                <Sheet
                {...sheet}
                onClose={setSheet}/>
            )}
            {notification && (
                <DesktopNotification
                utils={utils}
                notification={notification}
                onClose={setNotification}/>
            )}
            <BannerStack 
            banners={banners}
            onClose={id => setBanners(banners => banners.filter(banner => banner.id !== id))}
            utils={utils} />
            <AlertStack 
            alerts={alerts}
            onClose={setAlerts}
            utils={utils} />
            {datePickerOverride && (
                <DatePicker
                {...datePickerOverride}
                onClose={id => {
                    setDatePickerOverride(null);
                    if(typeof(datePickerOverride.onClose) === 'function') {
                        datePickerOverride.onClose();
                    }
                }} />
            )}
        </div>
    )
}

export default App;
